// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import { io } from 'socket.io-client';
import { renouvelerSession } from '../lib/session';

// ✅ FORCER PRODUCTION URL SI PAS DE ENV VAR
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL ||
                   (window.location.origin.includes('app.normascan.fr')
                     ? 'https://app.normascan.fr'
                     : 'http://localhost:5000');

console.log('🔧 [SOCKET] Initializing with URL:', SOCKET_URL);

let socket = null;

export const initSocket = () => {
  if (socket) {
    console.log('🔧 [SOCKET] Already initialized, reusing existing socket');
    return socket;
  }

  console.log('🔧 [SOCKET] Creating new socket connection...');
  
  socket = io(SOCKET_URL, {
    transports: ['polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionAttempts: 5,
    autoConnect: true,
    // Le cookie de session DOIT partir avec la poignée de main : c'est elle qui
    // dit au serveur qui écoute (chantier ⑯, blueprints/realtime.py). En
    // développement, le socket est sur une autre origine (port 5000).
    withCredentials: true,
    path: '/socket.io/',
  });

  socket.on('connect', () => {
    console.log('✅ [SOCKET] Connecté avec ID:', socket.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('❌ [SOCKET] Déconnecté:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('❌ [SOCKET] Erreur connexion:', error.message);
    console.error('❌ [SOCKET] URL tentée:', SOCKET_URL);
  });

  socket.on('reconnect_attempt', (attempt) => {
    console.log(`🔄 [SOCKET] Tentative de reconnexion ${attempt}...`);
  });

  socket.on('reconnect', (attempt) => {
    console.log(`✅ [SOCKET] Reconnecté après ${attempt} tentatives`);
  });

  return socket;
};

export const getSocket = () => {
  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    console.log('🔌 [SOCKET] Déconnecté manuellement');
  }
};

// Attend la connexion du socket (au plus `delai` ms).
const attendreConnexion = (s, delai = 5000) => new Promise((resolve, reject) => {
  if (s.connected) { resolve(); return; }
  const minuterie = setTimeout(() => reject(new Error('Socket non connecté')), delai);
  s.once('connect', () => { clearTimeout(minuterie); resolve(); });
});

// S'abonner à une analyse avec confirmation.
//
// L'identité est celle de la POIGNÉE DE MAIN (cookie de session, chantier ⑯) :
// un socket ouvert avant la connexion, ou avec un jeton expiré depuis, est
// anonyme. Le serveur répond alors `reconnexion_requise` : on renouvelle la
// session, on refait la poignée de main (avec les cookies d'aujourd'hui) et on
// réessaie UNE fois.
// ⚠️ Le MÊME objet socket, déconnecté puis reconnecté — jamais un nouveau :
// Dashboard.js y a accroché ses écouteurs de progression, qu'un socket neuf
// n'aurait pas.
export const subscribeToAnalysis = async (analysisId) => {
  try {
    return await abonner(analysisId);
  } catch (err) {
    if (err.code !== 'reconnexion_requise' || !socket) throw err;
    await renouvelerSession();
    socket.disconnect();
    socket.connect();
    await attendreConnexion(socket);
    return abonner(analysisId);
  }
};

const abonner = (analysisId) => {
  return new Promise((resolve, reject) => {
    if (!socket) {
      const error = 'Socket non initialisé';
      console.error('❌ [SUBSCRIBE]', error);
      reject(new Error(error));
      return;
    }

    if (!socket.connected) {
      const error = 'Socket non connecté (connected=false)';
      console.error('❌ [SUBSCRIBE]', error);
      reject(new Error(error));
      return;
    }

    console.log('📡 [SUBSCRIBE] Tentative abonnement pour:', analysisId);
    console.log('📡 [SUBSCRIBE] Socket ID:', socket.id);

    const timeout = setTimeout(() => {
      const error = 'Timeout abonnement (5s)';
      console.error('❌ [SUBSCRIBE]', error);
      reject(new Error(error));
    }, 5000);

    socket.emit('subscribe_analysis', {
      analysis_id: analysisId,
    }, (response) => {
      clearTimeout(timeout);
      
      console.log('📡 [SUBSCRIBE] Réponse reçue:', response);
      
      if (response && response.status === 'confirmed') {
        console.log('✅ [SUBSCRIBE] Abonnement confirmé');
        resolve(response);
      } else {
        const error = response?.error || 'Abonnement échoué';
        console.error('❌ [SUBSCRIBE] Refusé:', error);
        const erreur = new Error(error);
        erreur.code = response?.error_code;
        reject(erreur);
      }
    });
  });
};